import { useCallback, useEffect, useState } from 'react';
import { useMaybeRoomContext } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
import type { Participant, TranscriptionSegment } from 'livekit-client';

/**
 * One utterance from the live transcript stream. Mirrors the shape
 * documented in docs/client-frontend/live-transcript-frontend.md §2.
 *
 * `startMs` / `endMs` are monotonic offsets from the bot's subscription
 * start, NOT wall-clock. Use `receivedAt` if you need wall-clock.
 */
export interface LiveTranscriptSegment {
  id: string;
  text: string;
  language: string;
  startMs: number;
  endMs: number;
  final: boolean;
  participantIdentity: string;
  participantName: string;
  receivedAt: number;
}

export interface UseSessionTranscriptResult {
  segments: LiveTranscriptSegment[];
  reset: () => void;
}

/**
 * Subscribe to live transcript segments published into the LiveKit room
 * by the Sarvam-backed transcription bot. Must be called inside a
 * `<LiveKitRoom>` (or `<RoomContext.Provider>`); silently no-ops outside.
 *
 * `sessionId` is required. The buffer auto-resets when it changes —
 * cross-session contamination would leak PHI.
 *
 * Segments arrive in event order. Newest at the tail. The hook dedups by
 * segment id but defensively takes the latest copy if the same id arrives
 * twice (the backend invariant is "ids never updated", but we don't trust
 * it blindly).
 */
export function useSessionTranscript(
  sessionId: string | null | undefined,
): UseSessionTranscriptResult {
  const room = useMaybeRoomContext();
  const [segments, setSegments] = useState<LiveTranscriptSegment[]>([]);

  // PHI cross-contamination guard: reset segments when sessionId changes.
  // Uses the React docs' "store info from previous renders" pattern instead
  // of useEffect to satisfy react-hooks/set-state-in-effect.
  const [prevSessionId, setPrevSessionId] = useState(sessionId);
  if (prevSessionId !== sessionId) {
    setPrevSessionId(sessionId);
    setSegments([]);
  }

  useEffect(() => {
    if (!room) return;

    function handleTranscription(raw: TranscriptionSegment[], participant?: Participant) {
      const now = Date.now();
      const incoming: LiveTranscriptSegment[] = raw.map((s) => ({
        id: s.id,
        text: s.text,
        language: s.language,
        startMs: s.startTime,
        endMs: s.endTime,
        final: s.final,
        participantIdentity: participant?.identity ?? '',
        participantName:
          participant?.name && participant.name.length > 0
            ? participant.name
            : (participant?.identity ?? ''),
        receivedAt: now,
      }));

      setSegments((prev) => {
        if (incoming.length === 0) return prev;
        const byId = new Map(prev.map((seg) => [seg.id, seg]));
        for (const seg of incoming) byId.set(seg.id, seg);
        return Array.from(byId.values());
      });
    }

    room.on(RoomEvent.TranscriptionReceived, handleTranscription);
    return () => {
      room.off(RoomEvent.TranscriptionReceived, handleTranscription);
    };
  }, [room]);

  const reset = useCallback(() => {
    setSegments([]);
  }, []);

  return { segments, reset };
}
